/* Toshi size-finder — SHARED data layer.
 *
 * One home for the Airtable size-row contract, consumed by BOTH finders:
 *   - assets/toshi-pdp-size-finder.js          (<size-finder>, PDP)
 *   - assets/toshi-size-finder-chart-page.js   (<size-finder-ref>, size-chart pages)
 * Load this asset (defer) BEFORE either consumer script — defer scripts run in
 * document order, so a <script defer> tag above the consumer's is enough.
 *
 * ROW CONTRACT (one flat row per size, from the Airtable rollup):
 *   sku_size_code · size_name · age_low · age_high · age_band_period
 *   measure_primary · measure_secondary · sku_sort_order
 *   (+ size_finder_name, PDP only)
 * sku_sort_order is the series position (Airtable `SKU Sort Order`). When
 * EVERY row of a chart carries one it is the authoritative order; otherwise
 * the chart falls back to the legacy youngest-first age sort (all-or-nothing
 * per chart, so a partially numbered chart never half-sorts).
 * Ages are discrete, non-overlapping display values in the row's own unit
 * (age_band_period "months"/"years"); age_high null/absent/'' = open-ended
 * top band; age_low == age_high = a point age (AU clothing size 0 = 6 months).
 * Blank age_low = a CHIPLESS row: the size holds its sort position as an
 * alternate card and chart-table row but gets NO age chip (headwear XL — a
 * larger-head fit variant of 13+, not an age of its own). Chipless rows need
 * sort mode; under the age-sort fallback they are unorderable and dropped.
 * Measures are label-first strings: "height 84 cm", "head circ. 51-54 cm",
 * "weight 2-4 kg" (unit cm / kg / g). When weight is the primary card value it
 * goes in measure_primary and the length measures move to measure_secondary.
 *
 * This module owns ONLY the row→display derivation the two finders used to
 * reimplement separately (label/unit rules, measure splitting). Anything
 * component-specific stays in the consumer: the PDP's continuous month ladder,
 * variant bridge, modal and chart table; the chart page's advisory cards.
 *
 * Exposed as window.ToshiSizeFinderData (theme JS — no module system).
 */
(() => {
  const norm = (s) => String(s).trim().toLowerCase();

  /* Airtable rollup rows → display rows, in series order (sku_sort_order
   * when every row has one, else the legacy youngest-first age sort).
   *
   * Each returned row: {
   *   label      — age-chip text: "13+" (open top band), "1" (point age),
   *                "6–11" (range) — or null for a CHIPLESS row (blank
   *                age_low): the size keeps its place in the series but the
   *                consumer must not build an age chip for it
   *   unit       — age_band_period, singularised for a point age of exactly 1
   *                ("1 year", never "1 years"); '' for a chipless row
   *   hasBand    — false = chipless (label is null)
   *   size       — the DISPLAYED size token: size_name by default, or
   *                sku_size_code when sizeDisplay === 'size_code' (a per-chart
   *                switch — headwear shows the short code "XXS" because the
   *                names "Extra Extra Small" wrap badly in a tile; clothing
   *                shows the name "00"). This is display only; the PDP still
   *                BINDS variants on size_name (see intersectRows).
   *   code       — sku_size_code (stable short code, retained for reference)
   *   name       — size_name (the variant option value; kept for the bridge)
   *   measure    — measure_primary display string
   *   secondary  — measure_secondary display string
   *   loMonths   — age_low converted to months (null for a chipless row)
   *   hiMonths   — age_high in months, or null for an open-ended band or a
   *                chipless row
   *   sortOrder  — Number(sku_sort_order), or null when absent
   *   row        — the original Airtable row, untouched
   * }
   * Rows missing size_name are always dropped. An entered age_low of 0 =
   * active from birth and is kept. Blank age_low = chipless (kept in sort
   * mode, dropped under the age-sort fallback — it has no sort key there).
   * `sizeDisplay` picks which field the `size` token shows — 'size_code' or
   * 'size_name' (anything else / blank ⇒ size_name). Matched tolerantly so the
   * Airtable single-select label ("Size code") and the metaobject token
   * ("size_code") both resolve — spaces and underscores are folded to one form
   * before comparing, so "Size code" / "size_code" / "size code" all match.
   */
  const parseRows = (rows, sizeDisplay) => {
    const rate = (r) => (norm(r.age_band_period).indexOf('year') === 0 ? 12 : 1);
    const showCode = norm(sizeDisplay || '').replace(/[\s_]+/g, '') === 'sizecode';
    const mapped = rows
      .filter((r) => r && r.size_name != null && r.size_name !== '')
      .map((r) => {
        const k = rate(r);
        const hasBand = r.age_low != null && r.age_low !== '';
        const rawSort = r.sku_sort_order;
        const sortOrder = (rawSort == null || rawSort === '' || Number.isNaN(Number(rawSort)))
          ? null
          : Number(rawSort);
        const open = r.age_high == null || r.age_high === '';
        const point = hasBand && !open && Number(r.age_low) === Number(r.age_high);
        let label = null;
        let unit = '';
        if (hasBand) {
          if (open) label = `${r.age_low}+`;
          else if (point) label = `${r.age_low}`;
          else label = `${r.age_low}–${r.age_high}`;
          // "1 year" not "1 years" for a point age of exactly 1
          unit = r.age_band_period || '';
          if (point && Number(r.age_low) === 1) unit = unit.replace(/s\s*$/, '');
        }
        return {
          label,
          unit,
          hasBand,
          sortOrder,
          size: (showCode && r.sku_size_code != null && String(r.sku_size_code).trim() !== '')
            ? r.sku_size_code
            : r.size_name,
          code: r.sku_size_code || '',
          name: r.size_name || '',
          measure: r.measure_primary || '',
          secondary: r.measure_secondary || '',
          loMonths: hasBand ? Number(r.age_low) * k : null,
          hiMonths: hasBand && !open ? Number(r.age_high) * k : null,
          row: r,
        };
      });
    // sku_sort_order is authoritative only when EVERY row carries one —
    // all-or-nothing per chart, so a partially numbered chart can't half-sort.
    if (mapped.length && mapped.every((m) => m.sortOrder != null)) {
      return mapped.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    // legacy age sort: chipless rows have no key here, so they drop — which
    // also means a stale metaobject without sku_sort_order renders exactly as
    // it always did.
    return mapped.filter((m) => m.hasBand).sort((a, b) => a.loMonths - b.loMonths);
  };

  /* Exact intersection of a chart's full row superset with a Product's real
     size-option values (PDP only — a Page renders every row). Matching is
     EXACT on size_name, the canonical Shopify size-option value, after a
     whitespace trim; never substring, case-folded, nearest-size or fuzzy.
     size_name binds because Airtable authors it as BOTH the chart row's
     display name AND the Shopify variant Size option value (one field, one
     source), so they align by construction — where sku_size_code and the
     option value diverge (e.g. headwear "Small" vs code "S"), the name is the
     value the shopper actually sees and picks. Matching rows keep chart order;
     nonmatching superset rows are dropped. Inventory plays no part: a sold-out
     variant's value still matches — stock state is applied by the consumer
     after matching. Returns:
       rows            — matching rows, in chart order
       unmatchedValues — option values with no chart row (consumer emits a Dev
                         warning; the data issue must not be silently accepted)
       duplicateCodes  — size_names matched by more than one row (an ambiguous
                         chart violates the uniqueness rule) */
  const intersectRows = (rows, optionValues) => {
    const wanted = new Set(
      (optionValues || []).map((v) => String(v).trim()).filter((v) => v !== ''),
    );
    const seen = new Set();
    const duplicateCodes = [];
    const matched = (rows || []).filter((r) => {
      const nm = r && r.size_name != null ? String(r.size_name).trim() : '';
      if (!nm || !wanted.has(nm)) return false;
      if (seen.has(nm)) {
        if (!duplicateCodes.includes(nm)) duplicateCodes.push(nm);
      }
      seen.add(nm);
      return true;
    });
    const unmatchedValues = [...wanted].filter((v) => !seen.has(v));
    return { rows: matched, unmatchedValues, duplicateCodes };
  };

  /* Split a measure into descriptor + value so cards can stack the descriptor
     over the value. Airtable owns the label-first format: "height 76 cm",
     "head circ. 51-54 cm", "foot length 10.5 cm", "weight 2-4 kg". The unit is
     cm / kg / g (length + weight), matched longest-first so "kg" wins over "g". */
  const splitMeasure = (measure) => {
    const raw = String(measure || '').trim();
    if (!raw) return { value: '', descriptor: '' };
    const unitValue = '(\\d+(?:\\.\\d+)?(?:\\s*[–-]\\s*\\d+(?:\\.\\d+)?)?\\s*(?:cm|kg|g))';
    const labelFirst = raw.match(new RegExp(`^(.+?)\\s+${unitValue}\\.?$`, 'i'));
    if (labelFirst && !/^\d/.test(labelFirst[1].trim())) {
      return { value: labelFirst[2].trim(), descriptor: labelFirst[1].trim() };
    }
  // NEW: non-numeric measurements, labelled with a colon
  const labelled = raw.match(/^([^:]+?):\s*(.+)$/);
  if (labelled && !/\d/.test(labelled[1])) {
    return { value: labelled[2].trim(), descriptor: labelled[1].trim() };
  }
  return { value: raw, descriptor: '' };
};

  const escHtml = (t) => String(t).replace(/[&<>"]/g, (m) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));

  /* theme editor / unpublished-theme preview / *.myshopify.com /
     ?preview_theme_id / localhost — the ONE definition of "dev surface" both
     finders gate their dev warnings on. The theme-role check matters most:
     Shopify redirects previews to the primary domain and moves
     preview_theme_id into a session cookie, so the URL param alone misses a
     Dev-theme preview. Real storefront visitors (published theme, role
     "main") never match. */
  const isDevSurface = () => {
    const host = window.location.hostname || '';
    return !!(window.Shopify?.designMode
      || (window.Shopify?.theme && window.Shopify.theme.role !== 'main')
      || /myshopify\.com$/.test(host)
      || new URLSearchParams(window.location.search).has('preview_theme_id')
      || host === 'localhost' || host === '127.0.0.1');
  };

  /* Inner markup of a size card, shared by BOTH finders so the tiles can't
     drift: a small "size" label over the code, then the primary measure as
     descriptor-over-value on two lines (value only when the measure has no
     descriptor). Banners/labels around it stay wrapper-specific. */
  const cardInnerHtml = (sizeCode, primaryMeasure) => {
    const meas = splitMeasure(primaryMeasure);
    const measHtml = meas.descriptor
      ? `<span class="sf-scm__d">${escHtml(meas.descriptor)}</span><span class="sf-scm__v">${escHtml(meas.value)}</span>`
      : `<span class="sf-scm__v">${escHtml(meas.value)}</span>`;
    return `<span class="sf-spec">`
      + `<span class="sf-sk"><span class="sf-sk__lab">size</span><span class="sf-sk__v">${escHtml(sizeCode)}</span></span>`
      + `<span class="sf-scm">${measHtml}</span></span>`;
  };

  /* Inner markup of the struck-through EMPTY edge card (first/last size has no
     smaller/larger neighbour): echoes a real card's structure with invisible
     placeholders — including a 2-line measure when the reference measure has a
     descriptor — so the empty card matches the real cards' height exactly. */
  const emptyCardInnerHtml = (referenceMeasure) => {
    const measRows = splitMeasure(referenceMeasure).descriptor
      ? '<span class="sf-scm__d">&nbsp;</span><span class="sf-scm__v">&nbsp;</span>'
      : '<span class="sf-scm__v">&nbsp;</span>';
    return '<span class="sf-spec"><span class="sf-sk"><span class="sf-sk__lab">&nbsp;</span>'
      + `<span class="sf-sk__v">&nbsp;</span></span><span class="sf-scm">${measRows}</span></span>`;
  };

  /* Tab switching for the expandable tip card (Size Chart / How to measure),
     keyed by data-sf-tab ↔ data-sf-panel inside `root` — mark the named tab
     selected and show only its panel. Shared by both finders. */
  const selectTab = (root, name) => {
    root.querySelectorAll('[data-sf-tab]').forEach((t) => {
      t.setAttribute('aria-selected', String(t.dataset.sfTab === name));
    });
    root.querySelectorAll('[data-sf-panel]').forEach((p) => {
      p.hidden = p.dataset.sfPanel !== name;
    });
  };

  /* Decompose a size's measures into {dim, val} pairs for a size-chart table,
     so each dimension (height / chest / hip / head circ.) becomes its own column
     and the unit words are stated once as headers rather than repeated per cell.
     The primary is one pair; the secondary is a comma-joined string of more pairs
     ("chest 48 cm, hip 54 cm"). A measure with no descriptor (nested-data range
     like "38–46 cm") gets a generic dim so it still lands in a column. */
  const GENERIC_DIM = 'measurement';
  const measurePairs = (primary, secondary) => {
    const pairs = [];
    const push = (raw) => {
      const { value, descriptor } = splitMeasure(raw);
      if (value) pairs.push({ dim: descriptor || GENERIC_DIM, val: value });
    };
    push(primary);
    if (secondary) String(secondary).split(',').forEach((seg) => push(seg.trim()));
    return pairs;
  };

  /* Build the size-chart table into `el` from a list of sizes, one column per
     measurement dimension (height / chest / hip / head circ.) in first-seen
     order, one row per size. Shared by BOTH finders — the PDP's inline chart
     modal and the size-chart page's expandable chart — so the table markup and
     the `data-sf-row` hook markChartRow() keys on stay identical.

     `items` is [{ size, primary, secondary }] in display (row) order; `primary`
     / `secondary` are the label-first measure strings. Renders a
     `.sf-charttable`; each <tr> carries data-sf-row = its index for
     markChartRow(). Clears `el` if there's nothing to show. */
  const buildChartTable = (el, items) => {
    if (!el) return;
    const esc = (t) => String(t).replace(/[&<>"]/g, (m) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));

    const dims = [];
    const rows = (items || []).map((s, i) => {
      const pairs = measurePairs(s.primary, s.secondary);
      pairs.forEach((p) => { if (!dims.includes(p.dim)) dims.push(p.dim); });
      const byDim = {};
      pairs.forEach((p) => { byDim[p.dim] = p.val; });
      return { i, size: s.size, byDim };
    });
    if (!rows.length || !dims.length) { el.textContent = ''; return; }

    const head = `<tr><th scope="col">size</th>${
      dims.map((d) => `<th scope="col">${esc(d)}</th>`).join('')}</tr>`;
    const body = rows.map((r) => `<tr data-sf-row="${r.i}"><th scope="row">${esc(r.size)}</th>${
      dims.map((d) => `<td>${esc(r.byDim[d] || '—')}</td>`).join('')}</tr>`).join('');
    el.innerHTML = `<table class="sf-charttable"><thead>${head}</thead><tbody>${body}</tbody></table>`;
  };

  /* Highlight the chart row whose data-sf-row matches `idx` (the finder's
     current size index), clearing the rest — keeps the table and the size cards
     connected. Pass idx == null to clear all highlights. */
  const markChartRow = (el, idx) => {
    if (!el) return;
    el.querySelectorAll('[data-sf-row]').forEach((tr) => {
      tr.classList.toggle('is-current', idx != null && Number(tr.dataset.sfRow) === idx);
    });
  };

  /* Shared card-selection state for the PDP and standalone chart finder.
     The consumer owns how it derives a recommendation (PDP statistics vs. a
     chart row's 1:1 age mapping) and every UI/product side effect. This helper
     owns only the stable selection contract: an explicit pick overrides the
     recommendation; a recommendation change clears that explicit pick; and a
     card click toggles the current pick. The chart page deliberately retains a
     tick on its hero after deselection, whereas the PDP returns to an unticked
     hint, so that one visible policy is configured at construction. */
  const createSizeSelection = ({
    recommendedIdx = null,
    confirmed = false,
    retainConfirmationOnDeselect = false,
  } = {}) => {
    let recommendation = recommendedIdx;
    let explicit = null;
    let isConfirmed = confirmed;

    const currentIdx = () => (explicit != null ? explicit : recommendation);
    const snapshot = () => ({
      recommendedIdx: recommendation,
      explicitIdx: explicit,
      confirmed: isConfirmed,
      currentIdx: currentIdx(),
    });

    return {
      get recommendedIdx() { return recommendation; },
      get explicitIdx() { return explicit; },
      get confirmed() { return isConfirmed; },
      currentIdx,
      snapshot,
      // Recalculating a PDP hint must never discard an explicit card choice.
      setRecommendation(idx) {
        recommendation = idx;
        return snapshot();
      },
      // An age change chooses a new recommendation and confirms its hero.
      chooseRecommendation(idx) {
        recommendation = idx;
        explicit = null;
        isConfirmed = true;
        return snapshot();
      },
      // URL/variant restoration is an explicit selection, not an age change.
      setExplicit(idx, nextConfirmed = true) {
        explicit = idx;
        isConfirmed = nextConfirmed;
        return snapshot();
      },
      toggleExplicit(idx) {
        if (isConfirmed && idx === currentIdx()) {
          explicit = null;
          isConfirmed = retainConfirmationOnDeselect;
        } else {
          explicit = idx;
          isConfirmed = true;
        }
        return snapshot();
      },
      window(length) {
        const mostIdx = recommendation;
        return {
          downIdx: mostIdx != null && mostIdx > 0 ? mostIdx - 1 : null,
          mostIdx,
          upIdx: mostIdx != null && mostIdx + 1 < length ? mostIdx + 1 : null,
        };
      },
    };
  };

  window.ToshiSizeFinderData = {
    norm, parseRows, intersectRows, splitMeasure, measurePairs, buildChartTable, markChartRow,
    createSizeSelection, cardInnerHtml, emptyCardInnerHtml, selectTab, isDevSurface,
  };
})();
